import { FC, useState, createContext, useContext, useCallback, useRef } from 'react';

interface Message {
  role: 'user' | 'assistant' | 'tool';
  content: string;
  images?: string[];
  toolName?: string;
  toolResult?: any;
  isStreaming?: boolean;
}

interface Conversation {
  id: string;
  title: string;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
  source?: 'web' | 'whatsapp';
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

interface AgentContextType {
  // State
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  conversations: Conversation[];
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  currentConversationId: string | null;
  setCurrentConversationId: React.Dispatch<React.SetStateAction<string | null>>;
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  loadingConversations: boolean;
  setLoadingConversations: React.Dispatch<React.SetStateAction<boolean>>;

  // Caching
  getCachedConversations: () => Conversation[] | null;
  setCachedConversations: (data: Conversation[]) => void;
  getCachedConversation: (id: string) => any | null;
  setCachedConversation: (id: string, data: any) => void;
  invalidateCache: () => void;

  // State persistence
  isInitialized: boolean;
  setInitialized: (value: boolean) => void;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const AgentContext = createContext<AgentContextType>(
  {} as AgentContextType
);

export const useAgent = () => useContext(AgentContext);

export const AgentProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  // Persisted state
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  const [isInitialized, setInitialized] = useState(false);

  // Cache refs (don't trigger re-renders)
  const conversationsCache = useRef<CacheEntry<Conversation[]> | null>(null);
  const conversationCache = useRef<Map<string, CacheEntry<any>>>(new Map());

  const getCachedConversations = useCallback((): Conversation[] | null => {
    if (!conversationsCache.current) return null;

    const { data, timestamp } = conversationsCache.current;
    const isExpired = Date.now() - timestamp > CACHE_DURATION;

    if (isExpired) {
      conversationsCache.current = null;
      return null;
    }

    return data;
  }, []);

  const setCachedConversations = useCallback((data: Conversation[]) => {
    conversationsCache.current = {
      data,
      timestamp: Date.now()
    };
  }, []);

  const getCachedConversation = useCallback((id: string): any | null => {
    const cached = conversationCache.current.get(id);
    if (!cached) return null;

    const { data, timestamp } = cached;
    const isExpired = Date.now() - timestamp > CACHE_DURATION;

    if (isExpired) {
      conversationCache.current.delete(id);
      return null;
    }

    return data;
  }, []);

  const setCachedConversation = useCallback((id: string, data: any) => {
    conversationCache.current.set(id, {
      data,
      timestamp: Date.now()
    });
  }, []);

  const invalidateCache = useCallback(() => {
    conversationsCache.current = null;
    conversationCache.current.clear();
  }, []);

  return (
    <AgentContext.Provider
      value={{
        messages,
        setMessages,
        conversations,
        setConversations,
        currentConversationId,
        setCurrentConversationId,
        sidebarOpen,
        setSidebarOpen,
        isLoading,
        setIsLoading,
        loadingConversations,
        setLoadingConversations,
        getCachedConversations,
        setCachedConversations,
        getCachedConversation,
        setCachedConversation,
        invalidateCache,
        isInitialized,
        setInitialized
      }}
    >
      {children}
    </AgentContext.Provider>
  );
};
